/**
 * Color utility functions
 */
/**
 * Convert hex color to HSL
 */
function hexToHsl(hex) {
    hex = hex.replace(/^#/, '');
    if (hex.length === 3) {
        hex = hex
            .split('')
            .map((x) => x + x)
            .join('');
    }
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h;
    let s;
    const l = (max + min) / 2;
    if (max === min) {
        h = s = 0; // achromatic
    }
    else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
            default:
                h = 0;
        }
        h /= 6;
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
}
/**
 * Convert HSL to hex color
 */
function hslToHex(h, s, l) {
    h = h / 360;
    s = s / 100;
    l = l / 100;
    const hue2rgb = (p, q, t) => {
        if (t < 0)
            t += 1;
        if (t > 1)
            t -= 1;
        if (t < 1 / 6)
            return p + (q - p) * 6 * t;
        if (t < 1 / 2)
            return q;
        if (t < 2 / 3)
            return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    let r, g, b;
    if (s === 0) {
        r = g = b = l; // achromatic
    }
    else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    const toHex = (c) => {
        const hex = Math.round(c * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
/**
 * Convert hex to RGB
 */
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        }
        : null;
}
/**
 * Convert RGB to hex
 */
function rgbToHex(r, g, b) {
    const toHex = (c) => {
        const hex = Math.round(c).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
/**
 * Get contrasting text color (black or white) for a background color
 */
function getContrastingTextColor(backgroundColor) {
    const rgb = hexToRgb(backgroundColor);
    if (!rgb)
        return '#000000';
    // Calculate relative luminance
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
}
/**
 * Lighten a hex color by a percentage
 */
function lightenColor(hex, percent) {
    const hsl = hexToHsl(hex);
    hsl.l = Math.min(100, hsl.l + percent);
    return hslToHex(hsl.h, hsl.s, hsl.l);
}
/**
 * Darken a hex color by a percentage
 */
function darkenColor(hex, percent) {
    const hsl = hexToHsl(hex);
    hsl.l = Math.max(0, hsl.l - percent);
    return hslToHex(hsl.h, hsl.s, hsl.l);
}
/**
 * Generate a color palette from a base color
 */
function generatePalette(hexColor) {
    const hsl = hexToHsl(hexColor);
    const palette = new Set();
    // Generate lighter shades
    for (let i = 1; i <= 5; i++) {
        const lightness = Math.min(hsl.l + i * 10, 100);
        palette.add(hslToHex(hsl.h, hsl.s, lightness));
    }
    // Original color in the middle
    palette.add(hexColor);
    // Generate darker shades
    for (let i = 1; i <= 5; i++) {
        const lightness = Math.max(hsl.l - i * 10, 0);
        palette.add(hslToHex(hsl.h, hsl.s, lightness));
    }
    return Array.from(palette);
}

export { darkenColor, generatePalette, getContrastingTextColor, hexToHsl, hexToRgb, hslToHex, lightenColor, rgbToHex };
//# sourceMappingURL=colors.esm.js.map
